import { useTranslation } from "react-i18next";
import { CapsuleButton } from "../../Everou/_components";
import {
    EARLY_CHECKIN_STATE_BADGE,
    EarlyCheckinRequestState,
} from "../_models/PMSIntegration";
import { EarlyCheckinDecision } from "../_services/PMSReservationsService";

// State badge + confirm/reject/undo controls for a guest's early check-in
// request, shared by the global reservations list and the per-location
// reservation card so the two views can't drift. The parent owns the busy
// flag and the service call; this component only renders. `stacked` puts the
// wide confirm/reject pair on its own line below the badge (for narrow
// containers like the table cell); the ✕ undo is small enough to always sit
// beside the badge.
export function EarlyCheckinDecisionControls({
    state,
    canDecide,
    busy,
    onDecide,
    stacked = false,
}: {
    state: EarlyCheckinRequestState;
    canDecide: boolean;
    busy: boolean;
    onDecide: (decision: EarlyCheckinDecision) => void;
    stacked?: boolean;
}) {
    const { t } = useTranslation();
    const badge = EARLY_CHECKIN_STATE_BADGE[state];

    const badgeRow = (
        <span
            className="d-inline-flex align-items-center"
            style={{ columnGap: "6px" }}
        >
            <span
                data-testid="early-checkin-state-badge"
                className="badge badge-pill"
                style={{ backgroundColor: badge.color, color: "#fff" }}
                title={t(badge.titleKey)}
            >
                {t(badge.labelKey)}
            </span>
            {canDecide && (state === "confirmed" || state === "rejected") && (
                <CapsuleButton
                    testId="early-checkin-undo-button"
                    size="SMALL"
                    text="✕"
                    textUppercase={false}
                    style="DANGER"
                    tooltip={t(
                        state === "confirmed"
                            ? "mod_pms_reservation_early_checkin_unconfirm"
                            : "mod_pms_reservation_early_checkin_unreject",
                    )}
                    boostedToolTip
                    isEnabled={!busy}
                    onClick={() => onDecide(null)}
                />
            )}
        </span>
    );

    const decisionButtons = canDecide && state === "requested" && (
        <span
            className="d-inline-flex align-items-center"
            style={{ columnGap: "6px" }}
        >
            <CapsuleButton
                testId="early-checkin-confirm-button"
                size="SMALL"
                text={t("mod_pms_reservation_early_checkin_confirm_button")}
                tooltip={t("mod_pms_reservation_early_checkin_confirm")}
                boostedToolTip
                isEnabled={!busy}
                onClick={() => onDecide("confirmed")}
            />
            <CapsuleButton
                testId="early-checkin-reject-button"
                size="SMALL"
                style="INFO"
                text={t("mod_pms_reservation_early_checkin_reject_button")}
                tooltip={t("mod_pms_reservation_early_checkin_reject")}
                boostedToolTip
                isEnabled={!busy}
                onClick={() => onDecide("rejected")}
            />
        </span>
    );

    return (
        <span
            className={
                stacked
                    ? "d-inline-flex flex-column align-items-start"
                    : "d-inline-flex align-items-center"
            }
            style={stacked ? { rowGap: "4px" } : { columnGap: "6px" }}
        >
            {badgeRow}
            {decisionButtons}
        </span>
    );
}
